import {
  CalendarMonthOutlined,
  Campaign,
  CelebrationOutlined,
  ChevronLeft,
  ChevronRight,
  Diversity2,
  LogoutOutlined,
  OpenInNew,
  RouteOutlined,
  SettingsOutlined,
  Store,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  CardMedia,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import * as React from 'react';

import FairlyBackgroundLogo from '@/ui/assets/logo-fairly-background.png';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import { getInitialsFromName } from '@/ui/utils/initials-from-name';

import { Drawer } from './components/Drawer';
import { DrawerHeader } from './components/DrawerHeader';
import { listItemHoverStyle } from './Menu.utils';

interface IProps {
  userName: string;
  organisationLogo: string;
  currentLocationPath: string;
  disableSubscriptionButton: boolean;
  onClickSignOut: () => void;
  onClickMyProjects: () => void;
  onClickMyTours: () => void;
  onClickMyVenues: () => void;
  onClickMyResources: () => void;
  onClickMySurveys: () => void;
  onClickSettings: () => void;
  onClickSubscription: () => void;
}

export function Menu({
  userName,
  organisationLogo,
  currentLocationPath,
  disableSubscriptionButton,
  onClickSignOut,
  onClickMyProjects,
  onClickMyTours,
  onClickMyVenues,
  onClickMyResources,
  onClickMySurveys,
  onClickSettings,
  onClickSubscription,
}: IProps) {
  const [open, setOpen] = React.useState(false);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        variant="permanent"
        open={open}
        PaperProps={{ sx: { bgcolor: ColorsRaw.anthracite.Warm_Anthracite } }}
      >
        <DrawerHeader>
          <IconButton onClick={open ? handleDrawerClose : handleDrawerOpen}>
            {open ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        </DrawerHeader>
        <TopMenuView open={open} userName={userName} />
        {open && <OrganisationImage image={organisationLogo} />}
        <MiddleListNavigation
          open={open}
          currentLocationPath={currentLocationPath}
          onClickMyProjects={onClickMyProjects}
          onClickMyTours={onClickMyTours}
          onClickMyVenues={onClickMyVenues}
          onClickMyResources={onClickMyResources}
          onClickMySurveys={onClickMySurveys}
        />
        <Box sx={{ mt: 'auto' }}>
          <BottomList
            open={open}
            disableSubscriptionButton={disableSubscriptionButton}
            onClickSubscription={onClickSubscription}
            onClickSettings={onClickSettings}
            onClickSignOut={onClickSignOut}
          />
        </Box>
      </Drawer>
    </Box>
  );
}

function TopMenuView({ open, userName }: { open: boolean; userName: string }) {
  return (
    <List sx={{ px: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <ListItem disablePadding sx={{ mb: 1, ...listItemHoverStyle }}>
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 1.5,
              mr: open ? 0.5 : 0,
            }}
          >
            <Avatar
              sx={{
                bgcolor: ColorsRaw.anthracite.Warm_Anthracite_2,
                color: 'white',
                fontSize: '16px',
                mr: open ? 2 : 'auto',
                fontWeight: '600',
              }}
            >
              {getInitialsFromName(userName)}
            </Avatar>
            <ListItemText
              primary={userName}
              primaryTypographyProps={{
                fontSize: '14px',
                fontWeight: '500',
                maxWidth: '125px',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
              sx={{ opacity: open ? 1 : 0 }}
            />
          </ListItemButton>
        </ListItem>
      </Box>
    </List>
  );
}

function OrganisationImage({ image }: { image: string }) {
  const noImage = image === undefined || image === null || image === '';
  return (
    <Box
      sx={{
        my: 3,
        alignSelf: 'center',
        minWidth: '6rem',
        minHeight: '6rem',
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {noImage ? (
        <Box sx={{ width: '6rem', height: '6rem' }}>
          <img
            src={FairlyBackgroundLogo}
            alt={translate('Projects.project-image')}
            style={{
              padding: 10,
              objectFit: 'contain',
              width: '100%',
              height: '100%',
              backgroundColor: ColorsRaw.anthracite.Anthracite_2,
            }}
          />
        </Box>
      ) : (
        <CardMedia
          image={image}
          sx={{ width: '6rem', height: '6rem', borderRadius: '0.5rem' }}
        />
      )}
    </Box>
  );
}

function MiddleListNavigation({
  open,
  currentLocationPath,
  onClickMyProjects,
  onClickMyTours,
  onClickMyVenues,
  onClickMyResources,
  onClickMySurveys,
}: {
  open: boolean;
  currentLocationPath: string;
  onClickMyProjects: () => void;
  onClickMyTours: () => void;
  onClickMyVenues: () => void;
  onClickMyResources: () => void;
  onClickMySurveys: () => void;
}) {
  return (
    <List sx={{ mt: 2, mb: 2 }}>
      <MiddleListNavigationItem
        title={translate('Menu.my-events')}
        icon={<CelebrationOutlined />}
        selected={currentLocationPath.includes('/app/projects/festival')}
        onClick={onClickMyProjects}
        isDrawerOpen={open}
      />
      <MiddleListNavigationItem
        title={translate('Menu.my-venues')}
        icon={<CalendarMonthOutlined />}
        selected={currentLocationPath.includes('/app/projects/venue')}
        onClick={onClickMyVenues}
        isDrawerOpen={open}
      />
      <MiddleListNavigationItem
        title={translate('Menu.my-tours')}
        icon={<RouteOutlined />}
        selected={currentLocationPath.includes('/app/projects/tour')}
        onClick={onClickMyTours}
        isDrawerOpen={open}
      />
      <MiddleListNavigationItem
        title={translate('Menu.my-resources')}
        icon={<Diversity2 />}
        selected={false}
        onClick={onClickMyResources}
        isOpenInNewTab={true}
        isDrawerOpen={open}
      />
      <MiddleListNavigationItem
        title={translate('Menu.my-surveys')}
        icon={<Campaign />}
        selected={false}
        onClick={onClickMySurveys}
        isDrawerOpen={open}
      />
    </List>
  );
}

function MiddleListNavigationItem({
  title,
  icon,
  selected,
  isOpenInNewTab = false,
  onClick,
  isDrawerOpen,
}: {
  title: string;
  icon: React.ReactNode;
  selected: boolean;
  isOpenInNewTab?: boolean;
  onClick: () => void;
  isDrawerOpen: boolean;
}) {
  const NO_TOOLTIP = '';
  return (
    <Tooltip title={isDrawerOpen ? NO_TOOLTIP : title} placement="right" arrow>
      <ListItem
        disablePadding
        sx={{ display: 'block', ...listItemHoverStyle }}
        secondaryAction={
          isOpenInNewTab && isDrawerOpen && <OpenInNew fontSize="small" />
        }
      >
        <ListItemButton
          onClick={onClick}
          selected={selected}
          sx={{
            minHeight: 48,
            justifyContent: isDrawerOpen ? 'initial' : 'center',
            px: 2.5,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: isDrawerOpen ? 3 : 'auto',
              justifyContent: 'center',
            }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={title}
            primaryTypographyProps={{ fontSize: '14px', fontWeight: '500' }}
            sx={{ opacity: isDrawerOpen ? 1 : 0 }}
          />
        </ListItemButton>
      </ListItem>
    </Tooltip>
  );
}

function BottomList({
  open,
  disableSubscriptionButton,
  onClickSubscription,
  onClickSettings,
  onClickSignOut,
}: {
  open: boolean;
  disableSubscriptionButton: boolean;
  onClickSubscription: () => void;
  onClickSettings: () => void;
  onClickSignOut: () => void;
}) {
  return (
    <List>
      <BottomListItem
        title={translate('Menu.subscription')}
        icon={<Store />}
        onClick={onClickSubscription}
        isDrawerOpen={open}
        openInNewTab={true}
        disabled={disableSubscriptionButton}
      />
      <BottomListItem
        title={translate('Menu.settings')}
        icon={<SettingsOutlined />}
        onClick={onClickSettings}
        isDrawerOpen={open}
      />
      <BottomListItem
        title={translate('Menu.signout')}
        icon={<LogoutOutlined />}
        onClick={onClickSignOut}
        isDrawerOpen={open}
      />
    </List>
  );
}

function BottomListItem({
  title,
  icon,
  openInNewTab = false,
  onClick,
  isDrawerOpen,
  disabled = false,
}: {
  title: string;
  icon: React.ReactNode;
  openInNewTab?: boolean;
  onClick: () => void;
  isDrawerOpen: boolean;
  disabled?: boolean;
}) {
  const NO_TOOLTIP = '';
  return (
    <Tooltip title={isDrawerOpen ? NO_TOOLTIP : title} placement="right" arrow>
      <ListItem
        disablePadding
        sx={{ display: 'block', ...listItemHoverStyle }}
        secondaryAction={
          openInNewTab && isDrawerOpen && <OpenInNew fontSize="small" />
        }
      >
        <ListItemButton
          onClick={onClick}
          sx={{
            minHeight: 48,
            justifyContent: isDrawerOpen ? 'initial' : 'center',
            px: 2.5,
          }}
          disabled={disabled}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: isDrawerOpen ? 3 : 'auto',
              justifyContent: 'center',
            }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={title}
            primaryTypographyProps={{
              fontSize: '14px',
              fontWeight: '500',
            }}
            sx={{
              opacity: isDrawerOpen ? 1 : 0,
            }}
          />
        </ListItemButton>
      </ListItem>
    </Tooltip>
  );
}
